import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ContractorEditProfile() {
  const { user, refresh } = useAuth();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    bio: "", company: "", years_experience: "", service_zips: "",
    service_categories: [], notify_email: true, notify_sms: false,
    phone: "", website: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    if (user) {
      setForm({
        bio: user.bio || "",
        company: user.company || "",
        years_experience: user.years_experience || "",
        service_zips: (user.service_zips || []).join(", "),
        service_categories: user.service_categories || [],
        notify_email: user.notify_email ?? true,
        notify_sms: user.notify_sms ?? false,
        phone: user.phone || "",
        website: user.website || "",
      });
    }
  }, [user]);

  const toggleCat = (id) => {
    setForm((f) => ({
      ...f,
      service_categories: f.service_categories.includes(id)
        ? f.service_categories.filter((x) => x !== id)
        : [...f.service_categories, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put("/contractors/me/profile", {
        bio: form.bio || null,
        company: form.company || null,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        service_zips: form.service_zips.split(",").map((z) => z.trim()).filter(Boolean),
        service_categories: form.service_categories,
        notify_email: form.notify_email,
        notify_sms: form.notify_sms,
        phone: form.phone || null,
        website: form.website || null,
      });
      await refresh();
      toast.success("Profile saved");
      navigate("/contractor");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-3">// Pro profile</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Edit your profile</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">Tell customers about your work. Pick the trades and ZIPs you serve — we'll only notify you about matching leads.</p>

        <form onSubmit={submit} className="brut-card mt-8 p-8 bg-white space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Company / business name">
              <input className="brut-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="pp-company" />
            </Field>
            <Field label="Years experience">
              <input type="number" className="brut-input" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} data-testid="pp-years" />
            </Field>
          </div>
          <Field label="Bio (what you do, why customers should hire you)">
            <textarea className="brut-input min-h-[120px]" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="pp-bio" />
          </Field>

          <Field label="Trades you offer (notify-only for these)">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {cats.map((c) => (
                <button
                  key={c.category_id} type="button" onClick={() => toggleCat(c.category_id)}
                  className={`border-2 border-ink p-3 text-left font-mono text-xs uppercase tracking-widest font-bold ${form.service_categories.includes(c.category_id) ? "bg-klein text-white" : "bg-white hover:bg-acid"}`}
                  data-testid={`pp-cat-${c.slug}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <p className="font-mono text-[10px] mt-2 text-muted-foreground">Leave empty to receive all leads.</p>
          </Field>

          <Field label="Service ZIPs (comma-separated, leave empty for all)">
            <input className="brut-input" placeholder="78701, 78702, 78703" value={form.service_zips} onChange={(e) => setForm({ ...form, service_zips: e.target.value })} data-testid="pp-zips" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Phone (for SMS lead alerts)">
              <input className="brut-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 123 4567" data-testid="pp-phone" />
            </Field>
            <Field label="Website (optional)">
              <input className="brut-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." data-testid="pp-website" />
            </Field>
          </div>

          <div className="border-2 border-ink p-4 bg-bone">
            <div className="overline mb-3">// Notifications</div>
            <label className="flex items-center gap-3 text-sm font-mono uppercase tracking-widest">
              <input type="checkbox" checked={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.checked })} data-testid="pp-notify-email" />
              Email me when a matching lead is posted
            </label>
            <label className="flex items-center gap-3 text-sm font-mono uppercase tracking-widest mt-2">
              <input type="checkbox" checked={form.notify_sms} onChange={(e) => setForm({ ...form, notify_sms: e.target.checked })} data-testid="pp-notify-sms" />
              Text me when a matching lead is posted
            </label>
          </div>

          <button type="submit" className="brut-btn-primary" data-testid="pp-save">Save profile</button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    {children}
  </div>
);
